import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, ShoppingBag, Frame, Image as ImageIcon } from "lucide-react";
import { FadeIn } from "../components/FadeIn";
import { MagneticButton } from "../components/MagneticButton";
import { RoomVisualizer } from "../components/RoomVisualizer";
import { OrderModal } from "../components/OrderModal";
import {
  SHOP_PRODUCTS,
  SHOP_SIZES,
  SHOP_PAPERS,
  FREE_SHIPPING_THRESHOLD,
} from "../lib/images";
import { useT, usePageMeta } from "../lib/i18n";
import { useFormatEUR } from "../lib/format";
import { toast } from "sonner";

const SOON = false; // Stripe checkout enabled (legacy flag kept only for tests; no dead code)

export default function Boutique() {
  const t = useT();
  const formatEUR = useFormatEUR();
  usePageMeta({ titleKey: "meta.shop.title", descKey: "meta.shop.desc", path: "/boutique" });
  const CATEGORIES = [
    { k: "all",      l: t("shop.filter.all") },
    { k: "paysage",  l: t("shop.filter.paysage") },
    { k: "sport",    l: t("shop.filter.sport") },
    { k: "aventure", l: t("shop.filter.aventure") },
  ];
  const [cat, setCat] = useState("all");
  const [product, setProduct] = useState(null);

  const items = useMemo(
    () => (cat === "all" ? SHOP_PRODUCTS : SHOP_PRODUCTS.filter((p) => p.cat === cat)),
    [cat]
  );

  return (
    <main data-testid="boutique-page" className="bg-bone text-ink min-h-screen pt-32">
      {/* Hero */}
      <section className="container-x pb-12 border-b border-ink/15">
        <FadeIn>
          <div className="eyebrow mb-4" style={{ color: "#4A4E57" }}>{t("shop.eyebrow")}</div>
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <h1 className="md:col-span-7 font-display text-6xl md:text-[8rem] leading-[0.88] text-ink">
              {t("shop.heroA")} <span className="italic text-stone">{t("shop.heroIt")}</span> {t("shop.heroB")}
            </h1>
            <div className="md:col-span-5">
              <p className="text-ink/70 max-w-md mb-4">{t("shop.heroLead")}</p>
              <div className="text-[11px] tracking-[0.22em] uppercase text-stone">
                {t("shop.freeShipping")} {formatEUR(FREE_SHIPPING_THRESHOLD)}
              </div>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* Filter & spec strip */}
      <section className="container-x py-10 grid md:grid-cols-2 gap-8 items-start border-b border-ink/15">
        <div className="flex flex-wrap gap-3">
          {CATEGORIES.map((c) => (
            <button
              key={c.k}
              data-testid={`shop-cat-${c.k}`}
              onClick={() => setCat(c.k)}
              className={`text-[11px] tracking-[0.22em] uppercase px-4 py-2 border transition-colors ${
                cat === c.k
                  ? "bg-ink text-bone border-ink"
                  : "bg-transparent text-ink border-ink/20 hover:border-ink"
              }`}
            >
              {c.l}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-4 text-xs text-ink/70">
          <div>
            <div className="eyebrow mb-1" style={{ color: "#4A4E57" }}>{t("shop.strip.paper")}</div>
            <div>{t("shop.strip.paperVal")}</div>
          </div>
          <div>
            <div className="eyebrow mb-1" style={{ color: "#4A4E57" }}>{t("shop.strip.production")}</div>
            <div>{t("shop.strip.productionVal")}</div>
          </div>
          <div>
            <div className="eyebrow mb-1" style={{ color: "#4A4E57" }}>{t("shop.strip.delay")}</div>
            <div>{t("shop.strip.delayVal")}</div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="container-x py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {items.map((p, i) => (
            <FadeIn key={p.id} delay={(i % 6) * 0.06}>
              <button
                data-testid={`shop-item-${p.id}`}
                data-cursor="VOIR"
                onClick={() => setProduct(p)}
                className="text-left group w-full"
              >
                <div className="relative overflow-hidden bg-ink/5">
                  <img
                    src={p.img}
                    alt={p.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full aspect-[3/4] object-cover group-hover:scale-[1.04] transition-transform duration-[1200ms] ease-out opacity-0"
                    onLoad={(e) => e.currentTarget.classList.remove("opacity-0")}
                    style={{ transition: "opacity 500ms ease, transform 1200ms ease-out" }}
                  />
                  {/* Order CTA pill — appears at the bottom of the photo, lifts on hover */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
                    <span
                      data-testid={`shop-order-cta-${p.id}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-bone/95 text-ink text-[11px] tracking-[0.22em] uppercase font-medium shadow-[0_10px_28px_-6px_rgba(0,0,0,0.5)] backdrop-blur-sm transition-all duration-500 ease-out translate-y-1 opacity-95 group-hover:-translate-y-1 group-hover:opacity-100 group-hover:bg-ink group-hover:text-bone"
                    >
                      <ShoppingBag size={13} />
                      <span>{t("shop.card.orderCTA")}</span>
                    </span>
                  </div>
                </div>
                <div className="mt-4 flex justify-between items-end border-b border-ink/15 pb-3">
                  <div>
                    <div className="font-display text-2xl text-ink">{p.title}</div>
                    <div className="text-[11px] tracking-[0.2em] uppercase text-ink/50 mt-1">
                      {t("shop.card.paperLine")}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-ink/50">{t("shop.card.from")}</div>
                    <div className="font-display italic text-2xl text-stone">
                      {formatEUR(SHOP_SIZES[0].price)}
                    </div>
                  </div>
                </div>
              </button>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Bottom info */}
      <section className="bg-ink text-bone section-pad">
        <div className="container-x grid md:grid-cols-3 gap-12">
          <FadeIn>
            <div className="eyebrow mb-3">{t("shop.bottom.paper.eyebrow")}</div>
            <h3 className="font-display text-3xl mb-4">{t("shop.bottom.paper.title")}</h3>
            <p className="text-bone/70 leading-relaxed">{t("shop.bottom.paper.text")}</p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="eyebrow mb-3">{t("shop.bottom.noframe.eyebrow")}</div>
            <h3 className="font-display text-3xl mb-4">{t("shop.bottom.noframe.title")}</h3>
            <p className="text-bone/70 leading-relaxed">{t("shop.bottom.noframe.text")}</p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="eyebrow mb-3">{t("shop.bottom.limited.eyebrow")}</div>
            <h3 className="font-display text-3xl mb-4">{t("shop.bottom.limited.title")}</h3>
            <p className="text-bone/70 leading-relaxed">{t("shop.bottom.limited.text")}</p>
          </FadeIn>
        </div>
      </section>

      {/* Product modal */}
      <AnimatePresence>
        {product && (
          <ProductModal product={product} onClose={() => setProduct(null)} t={t} />
        )}
      </AnimatePresence>
    </main>
  );
}

function ProductModal({ product, onClose, t }) {
  const formatEUR = useFormatEUR();
  const [size, setSize] = useState(SHOP_SIZES[1]);
  const [paper, setPaper] = useState(SHOP_PAPERS[0]);
  const [limited, setLimited] = useState(false);
  const [view, setView] = useState("visualizer"); // "photo" | "visualizer" — open visualizer by default to showcase interactivity
  const [showOrder, setShowOrder] = useState(false);

  const isLimitedEligible = size.key === "60x90" || size.key === "80x120";
  const limitedSurcharge = limited && isLimitedEligible ? 50 : 0;
  const price = size.price + limitedSurcharge;
  const shipping = price >= FREE_SHIPPING_THRESHOLD ? 0 : size.shipping;
  const total = price + shipping;
  const freeShip = price >= FREE_SHIPPING_THRESHOLD;
  const progress = Math.min(100, (price / FREE_SHIPPING_THRESHOLD) * 100);
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - price);

  const handlePreOrder = () => {
    setShowOrder(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] bg-ink/95 flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="bg-bone text-ink w-full max-w-6xl max-h-[92vh] overflow-y-auto grid md:grid-cols-2 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="shop-modal-close"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 text-ink hover:text-stone"
        >
          <X size={22} />
        </button>

        {/* LEFT — toggle between photo + visualizer */}
        <div className="bg-ink p-5 md:p-7 flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex gap-1 self-start bg-bone/5 p-1 rounded-full text-[10px] tracking-[0.22em] uppercase">
              <button
                data-testid="view-photo"
                onClick={() => setView("photo")}
                className={`px-3.5 py-1.5 rounded-full flex items-center gap-2 transition-colors ${
                  view === "photo" ? "bg-stone text-ink" : "text-bone/70 hover:text-bone"
                }`}
              >
                <ImageIcon size={11} /> {t("shop.modal.viewPhoto")}
              </button>
              <button
                data-testid="view-visualizer"
                onClick={() => setView("visualizer")}
                className={`relative px-3.5 py-1.5 rounded-full flex items-center gap-2 transition-colors ${
                  view === "visualizer"
                    ? "bg-stone text-ink"
                    : "text-bone hover:text-bone bg-emerald-500/15 ring-1 ring-emerald-400/60 shadow-[0_0_22px_rgba(16,185,129,0.35)]"
                }`}
              >
                {view !== "visualizer" && (
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                  </span>
                )}
                <Frame size={11} /> {t("shop.modal.viewRoom")}
              </button>
            </div>
            {view !== "visualizer" && (
              <span className="hidden md:inline text-[9px] tracking-[0.22em] uppercase text-emerald-300/90 animate-pulse">
                {t("shop.modal.tryIt")}
              </span>
            )}
          </div>

          {view === "photo" ? (
            <div className="flex-1 flex items-center justify-center">
              <img
                src={product.img}
                alt={product.title}
                className="w-full max-h-[68vh] object-contain"
              />
            </div>
          ) : (
            <RoomVisualizer
              imageSrc={product.img}
              sizeKey={size.key}
              productTitle={product.title}
            />
          )}
        </div>

        <div className="p-6 md:p-12 space-y-6">
          <div>
            <div className="eyebrow mb-2" style={{ color: "#4A4E57" }}>{t("shop.modal.eyebrow")}</div>
            <h2 className="font-display text-4xl md:text-5xl">{product.title}</h2>
            <div className="text-xs text-ink/60 mt-2">{t("shop.modal.paperNote")}</div>
          </div>

          <div className="border-t border-ink/15 pt-6">
            <div className="eyebrow mb-3" style={{ color: "#4A4E57" }}>{t("shop.modal.format")}</div>
            <div className="grid grid-cols-3 gap-2">
              {SHOP_SIZES.map((s) => (
                <button
                  key={s.key}
                  data-testid={`size-${s.key}`}
                  onClick={() => setSize(s)}
                  className={`py-3 px-3 text-left border transition-all ${
                    size.key === s.key
                      ? "border-ink bg-ink text-bone"
                      : "border-ink/20 hover:border-ink"
                  }`}
                >
                  <div className="text-sm">{s.label}</div>
                  <div className={`text-xs ${size.key === s.key ? "text-bone/70" : "text-ink/50"}`}>
                    {formatEUR(s.price)}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-ink/15 pt-6">
            <div className="eyebrow mb-3" style={{ color: "#4A4E57" }}>{t("shop.modal.paper")}</div>
            <div className="grid grid-cols-2 gap-2">
              {SHOP_PAPERS.map((p) => (
                <button
                  key={p.key}
                  data-testid={`paper-${p.key}`}
                  onClick={() => setPaper(p)}
                  className={`p-4 text-left border transition-all ${
                    paper.key === p.key
                      ? "border-ink bg-ink text-bone"
                      : "border-ink/20 hover:border-ink"
                  }`}
                >
                  <div className="font-display text-xl">{p.label}</div>
                  <div className={`text-xs mt-1 leading-snug ${paper.key === p.key ? "text-bone/70" : "text-ink/50"}`}>
                    {t(p.descKey)}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {isLimitedEligible && (
            <div className="border-t border-ink/15 pt-6">
              <label className="flex items-start gap-3 cursor-none">
                <input
                  data-testid="limited-toggle"
                  type="checkbox"
                  checked={limited}
                  onChange={(e) => setLimited(e.target.checked)}
                  className="mt-1 accent-stone"
                />
                <div>
                  <div className="text-sm">{t("shop.modal.limited")}</div>
                  <div className="text-xs text-ink/55">+{formatEUR(50)} · {t("shop.modal.limitedNote")}</div>
                </div>
              </label>
            </div>
          )}

          <div className="border-t border-ink/15 pt-6 space-y-3">
            <div className="flex justify-between items-baseline">
              <div className="text-sm text-ink/70">{t("shop.modal.print")}</div>
              <div className="font-display text-2xl italic">{formatEUR(price)}</div>
            </div>
            <div className="flex justify-between items-baseline text-sm">
              <div className="text-ink/70">{t("shop.modal.shipping")}</div>
              <div>{freeShip ? <span className="text-stone">{t("shop.modal.free")}</span> : formatEUR(size.shipping)}</div>
            </div>
            <div className="flex justify-between items-baseline border-t border-ink/15 pt-3">
              <div className="eyebrow" style={{ color: "#4A4E57" }}>{t("shop.modal.total")}</div>
              <motion.div
                key={total}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="font-display text-3xl text-ink"
              >
                {formatEUR(freeShip ? price : total)}
              </motion.div>
            </div>

            {!freeShip && (
              <div>
                <div className="h-1 bg-ink/10 overflow-hidden">
                  <div
                    className="h-full bg-stone transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-ink/55 mt-2">
                  {t("shop.modal.remaining")} {formatEUR(remaining)} {t("shop.modal.forFreeShip")}
                </div>
              </div>
            )}
          </div>

          <MagneticButton
            data-testid="shop-preorder"
            onClick={handlePreOrder}
            className="btn-line dark w-full justify-center mt-4"
          >
            <Check size={14} />
            <span>{t("shop.modal.addCart")}</span>
          </MagneticButton>

          <div className="text-xs text-ink/55 leading-relaxed">
            {t("shop.modal.footNote")}
          </div>
        </div>
      </motion.div>
      {showOrder && (
        <OrderModal
          product={product}
          size={size}
          paper={paper}
          frame={{ key: "standard", label: "Standard" }}
          total={total}
          onClose={() => setShowOrder(false)}
        />
      )}
    </motion.div>
  );
}
